import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  TextField,
} from '@mui/material';
import { ArrowBack, Send, PersonAdd } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { examinationAPI, studentAPI, batchAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

const StudentAssignment = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [selectedExamination, setSelectedExamination] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedBatch, setSelectedBatch] = useState('');
  const [includeFailed, setIncludeFailed] = useState(false);

  // Data lists
  const [examinations, setExaminations] = useState([]);
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [studentExamRegistrations, setStudentExamRegistrations] = useState([]);

  // Pagination
  const PAGE_SIZE = 120;
  const [page, setPage] = useState(0);
  const [totalStudents, setTotalStudents] = useState(0);

  // Fetch examinations and batches on mount
  useEffect(() => {
    fetchExaminations();
    fetchBatches();
  }, []);

  // Lock the course to the selected examination's own course - it must never
  // be picked independently, that's what let DCA students get assigned to
  // DCA/MSO exams (and vice versa) with no warning.
  useEffect(() => {
    const exam = examinations.find(e => e._id === selectedExamination);
    setSelectedCourse(exam?.course?._id || '');
  }, [selectedExamination, examinations]);

  // Fetch exam registrations when examination is selected
  useEffect(() => {
    if (selectedExamination) {
      fetchExamRegistrations();
    }
  }, [selectedExamination]);

  // Fetch students when filters are set
  useEffect(() => {
    if (selectedExamination && selectedCourse) {
      fetchStudents(0);
    }
  }, [selectedExamination, selectedCourse, selectedBatch, includeFailed]);

  const fetchBatches = async () => {
    try {
      const res = await batchAPI.getAll();
      setBatches(res.data.data?.batches || []);
    } catch {
      console.error('Failed to load batches');
    }
  };

  const fetchExaminations = async () => {
    try {
      const response = await examinationAPI.getAll({ isActive: true });
      const allExams = response.data.data || response.data.examinations || [];

      // Filter out completed, cancelled, and expired examinations
      const now = new Date();
      // Add 24 hour grace period for registration deadline (in milliseconds)
      const gracePeriod = 24 * 60 * 60 * 1000;

      const validExams = allExams.filter(exam => {
        // Exclude cancelled or completed status
        if (exam.status === 'cancelled' || exam.status === 'completed') {
          return false;
        }

        // Exclude exams where registration deadline has passed (with grace period)
        const registrationDeadline = exam.registrationDeadline ? new Date(exam.registrationDeadline) : new Date(exam.endDate);
        const deadlineWithGrace = new Date(registrationDeadline.getTime() + gracePeriod);

        if (deadlineWithGrace < now) {
          return false;
        }

        return true;
      });

      setExaminations(validExams);
    } catch (error) {
      console.error('Error fetching examinations:', error);
      toast.error('Failed to load examinations');
    }
  };

  const fetchExamRegistrations = async () => {
    try {
      const response = await examinationAPI.getExamStudents(selectedExamination);
      const registrations = response.data.data?.studentExams || response.data.studentExams || [];
      setStudentExamRegistrations(registrations);
    } catch (error) {
      console.error('Error fetching exam registrations:', error);
      setStudentExamRegistrations([]);
    }
  };

  const fetchStudents = async (newPage = page) => {
    try {
      setLoading(true);
      const params = {
        course: selectedCourse,
        status: 'approved',
        centre: user.branch,
        limit: PAGE_SIZE,
        page: newPage + 1,
      };

      if (selectedBatch) {
        params.batch = selectedBatch;
      }

      const response = await studentAPI.getAll(params);
      const studentsList = response.data.data || response.data.students || [];
      const pagination = response.data.pagination || {};

      setStudents(studentsList);
      setTotalStudents(pagination.total || studentsList.length);
      setPage(newPage);
      setSelectedStudents([]);
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedStudents(students.map(s => s._id));
    } else {
      setSelectedStudents([]);
    }
  };

  const handleSelectStudent = (studentId) => {
    setSelectedStudents(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  const handleSubmit = async () => {
    if (!selectedExamination) {
      toast.error('Please select an examination');
      return;
    }

    if (selectedStudents.length === 0) {
      toast.error('Please select at least one student');
      return;
    }

    try {
      setSubmitting(true);
      await examinationAPI.assignStudents(selectedExamination, selectedStudents);
      toast.success(`${selectedStudents.length} students assigned successfully and submitted for approval`);

      // Reset form
      setSelectedStudents([]);
      fetchExamRegistrations(); // Refresh exam registrations to show new pending statuses
    } catch (error) {
      console.error('Error assigning students:', error);
      toast.error(error.response?.data?.message || 'Failed to assign students');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedExam = examinations.find(e => e._id === selectedExamination);

  // Get exam registration status for a student
  const getExamRegistrationStatus = (studentId) => {
    const registration = studentExamRegistrations.find(
      reg => reg.student?._id === studentId || reg.student === studentId
    );

    if (!registration) {
      return { status: 'not_registered', label: 'Not Registered' };
    }

    switch (registration.approvalStatus) {
      case 'pending':
        return { status: 'pending', label: 'Pending Approval' };
      case 'approved':
        return { status: 'approved', label: 'Approved' };
      case 'rejected':
        return { status: 'rejected', label: 'Rejected' };
      default:
        return { status: 'unknown', label: 'Unknown' };
    }
  };

  // Get color for exam registration status
  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      case 'not_registered':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/admin/examinations')}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Assign Students to Examination
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select students to assign for examination approval
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Selection Criteria
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Examination</InputLabel>
              <Select
                value={selectedExamination}
                onChange={(e) => {
                  setSelectedExamination(e.target.value);
                  setSelectedBatch('');
                  setStudents([]);
                  setTotalStudents(0);
                  setPage(0);
                }}
                label="Examination"
              >
                {examinations.map((exam) => (
                  <MenuItem key={exam._id} value={exam._id}>
                    {exam.name} ({exam.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            {/* Course is derived from the selected examination, never picked
                independently - that decoupling is what caused students to be
                assigned to an examination of the wrong course. */}
            <FormControl fullWidth required disabled>
              <InputLabel shrink={!!selectedCourse}>Course</InputLabel>
              <Select value={selectedCourse} label="Course">
                {selectedExam?.course && (
                  <MenuItem value={selectedExam.course._id}>
                    {selectedExam.course.name} ({selectedExam.course.code})
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={!selectedCourse}>
              <InputLabel>Batch (Optional)</InputLabel>
              <Select
                value={selectedBatch}
                onChange={(e) => setSelectedBatch(e.target.value)}
                label="Batch (Optional)"
                MenuProps={{ PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } } }}
              >
                <MenuItem value="">All Batches</MenuItem>
                {batches.map((b) => (
                  <MenuItem key={b._id} value={b._id}>
                    {b.name} ({b.year})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <Checkbox
                  checked={includeFailed}
                  onChange={(e) => setIncludeFailed(e.target.checked)}
                  disabled={!selectedCourse}
                />
                <Typography>Include students who failed previous examinations</Typography>
              </Box>
            </FormControl>
          </Grid>
        </Grid>

        {selectedExam && (
          <Alert severity="info" sx={{ mt: 3 }}>
            <strong>Examination Details:</strong> {selectedExam.name} ({selectedExam.code})
            <br />
            <strong>Duration:</strong> {new Date(selectedExam.startDate).toLocaleDateString()} - {new Date(selectedExam.endDate).toLocaleDateString()}
            <br />
            <strong>Subjects:</strong> {selectedExam.subjects?.length || 0}
          </Alert>
        )}
      </Paper>

      {/* Students Table */}
      {selectedCourse && (
        <Paper elevation={2} sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Students ({totalStudents})
            </Typography>
            {selectedStudents.length > 0 && (
              <Stack direction="row" spacing={2}>
                <Chip
                  label={`${selectedStudents.length} Selected`}
                  color="primary"
                  variant="outlined"
                />
                <Button
                  variant="contained"
                  startIcon={<Send />}
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? 'Submitting...' : 'Submit for Approval'}
                </Button>
              </Stack>
            )}
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : students.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedStudents.length === students.length}
                        indeterminate={selectedStudents.length > 0 && selectedStudents.length < students.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell><strong>Register No.</strong></TableCell>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Batch</strong></TableCell>
                    <TableCell><strong>Course</strong></TableCell>
                    <TableCell><strong>Exam Registration Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {students.map((student) => {
                    const examStatus = getExamRegistrationStatus(student._id);
                    const isAlreadyRegistered = examStatus.status !== 'not_registered';

                    return (
                      <TableRow
                        key={student._id}
                        hover={!isAlreadyRegistered}
                        onClick={() => !isAlreadyRegistered && handleSelectStudent(student._id)}
                        sx={{
                          cursor: isAlreadyRegistered ? 'default' : 'pointer',
                          opacity: isAlreadyRegistered ? 0.6 : 1
                        }}
                      >
                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedStudents.includes(student._id)}
                            onChange={() => handleSelectStudent(student._id)}
                            disabled={isAlreadyRegistered}
                          />
                        </TableCell>
                      <TableCell>{student.formNumber || student.registerNumber || 'N/A'}</TableCell>
                      <TableCell>{student.personalInfo?.nameAsPerTC || student.personalInfo?.fullName || 'N/A'}</TableCell>
                      <TableCell>{student.batch?.name || 'N/A'}</TableCell>
                      <TableCell>{student.course?.name || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip
                          label={examStatus.label}
                          size="small"
                          color={getStatusColor(examStatus.status)}
                        />
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <PersonAdd sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.3 }} />
              <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                No students found. Please adjust your filters.
              </Typography>
            </Box>
          )}

          {totalStudents > 0 && (
            <TablePagination
              component="div"
              count={totalStudents}
              page={page}
              onPageChange={(_, newPage) => fetchStudents(newPage)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
            />
          )}
        </Paper>
      )}
    </Box>
  );
};

export default StudentAssignment;
